import { useEffect, useState } from 'react';
import { Lock, X } from 'lucide-react';
import type { AppRecord } from '../lib/types';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../hooks/useToast';
import { ApiError, apiSend } from '../lib/api';

type Props = {
  app: AppRecord | null;
  onClose: () => void;
};

const messageFromError = (err: unknown): string => {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | undefined;
    return body?.error ?? `request failed (${err.status})`;
  }
  return err instanceof Error ? err.message : 'request failed';
};

export const RequestAccessModal = ({ app, onClose }: Props) => {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state whenever a new app is opened.
  useEffect(() => {
    if (app) {
      setNote('');
      setError(null);
      setSubmitting(false);
    }
  }, [app]);

  // Esc closes.
  useEffect(() => {
    if (!app) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [app, onClose]);

  if (!app) return null;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await apiSend<{ alreadyPending: boolean }>(
        'POST',
        `/api/apps/${app.id}/request-access`,
        { note: note.trim() || undefined },
        token,
      );
      toast({
        kind: 'success',
        message: res.alreadyPending
          ? `You already have a pending request for ${app.name}.`
          : `Request sent. An admin will review and get back to you.`,
      });
      onClose();
    } catch (err) {
      setError(messageFromError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/30 p-4 sm:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Request access to ${app.name}`}
    >
      <div
        className="max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto rounded-xl border border-line bg-bg-primary p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-bg-secondary text-text-tertiary">
              <Lock className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <h2 className="text-lg font-semibold leading-tight">Request access</h2>
              <p className="mt-0.5 text-sm text-text-secondary">
                You don't have access to <strong>{app.name}</strong> yet.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-9 w-9 shrink-0 place-items-center rounded text-text-tertiary hover:bg-bg-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="space-y-3"
        >
          <label className="block">
            <span className="text-xs font-medium text-text-secondary">
              Why do you need access? (optional)
            </span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="e.g. Need to view weekly claims report for my team's standup."
              className="mt-1 w-full rounded-md border border-line bg-bg-primary px-3 py-2 text-sm outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
            <span className="mt-1 block text-[10px] text-text-tertiary">
              Helps the admin decide. {note.length}/500
            </span>
          </label>

          {error && (
            <div className="rounded-md bg-status-critical/10 p-2 text-xs text-status-critical">
              {error === 'already_has_access'
                ? "You already have access to this app — try refreshing the page."
                : error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-line px-3 py-1.5 text-sm hover:bg-bg-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-contrast hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? 'Sending...' : 'Send request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
